import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import { SetAdminTrialOutreachOutcomeAPI } from '../../../../api/AdminAPI';
import { OUTCOME_OPTIONS, REASON_OPTIONS, NOTE_MAX_LENGTH } from '../../trial-outreach/trialOutreachOutcomes';

/**
 * CompanyOutreachOutcomeDialog - Marca o resultado do contato com a empresa (Admin Geral).
 * Descartado pede o motivo e tira a empresa da rotina de contato com testes.
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {{ id: number|string, name: string }|null} props.company
 * @param {{ outcome: string, reason?: string|null, note?: string|null }|null} props.current
 * @param {Function} props.onSaved recebe o resultado salvo (ou null quando limpo)
 * @param {Function} props.onCancel
 */
function CompanyOutreachOutcomeDialog({ isOpen, company, current, onSaved, onCancel }) {
  const [outcome, setOutcome] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setOutcome(current?.outcome || '');
    setReason(current?.reason || '');
    setNote(current?.note || '');
    setError('');
  }, [isOpen, current]);

  const isDiscarded = outcome === 'discarded';
  const canSave = !saving && (outcome !== '' || current) && (!isDiscarded || reason !== '');

  const handleConfirm = () => {
    setSaving(true);
    setError('');
    SetAdminTrialOutreachOutcomeAPI(
      {
        company_id: company.id,
        outcome: outcome || 'none',
        reason: isDiscarded ? reason : '',
        note: outcome ? note : '',
      },
      (response) => {
        setSaving(false);
        if (!response?.success) {
          setError(response?.error || 'Não foi possível salvar.');
          return;
        }
        onSaved(response.outcome || null);
      }
    );
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title="Resultado do contato"
      variant="info"
      confirmLabel={outcome === '' && current ? 'Limpar resultado' : 'Salvar'}
      cancelLabel="Cancelar"
      onConfirm={handleConfirm}
      onCancel={onCancel}
      confirmDisabled={!canSave}
      loading={saving}
      maxWidth={520}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {company?.name && (
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {company.name}
          </Typography>
        )}

        <Box>
          <ToggleButtonGroup
            value={outcome}
            exclusive
            size="small"
            onChange={(e, value) => setOutcome(value || '')}
            disabled={saving}
          >
            {OUTCOME_OPTIONS.map((item) => (
              <ToggleButton key={item.value} value={item.value} sx={{ px: 2, textTransform: 'none' }}>
                {item.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {isDiscarded
              ? 'A rotina de contato com testes não escreve mais para esta empresa.'
              : 'Clique de novo no escolhido para limpar.'}
          </Typography>
        </Box>

        {isDiscarded && (
          <TextField
            select
            size="small"
            label="Motivo"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            disabled={saving}
            // O ConfirmDialog fica em z-index 9999; o menu precisa abrir acima dele.
            SelectProps={{ MenuProps: { sx: { zIndex: 10000 } } }}
          >
            {REASON_OPTIONS.map((item) => (
              <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
            ))}
          </TextField>
        )}

        {outcome !== '' && (
          <TextField
            label="Observação (opcional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            disabled={saving}
            inputProps={{ maxLength: NOTE_MAX_LENGTH }}
            helperText={`${note.length}/${NOTE_MAX_LENGTH}`}
          />
        )}

        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    </ConfirmDialog>
  );
}

export default CompanyOutreachOutcomeDialog;
